import { useState, useCallback, useRef, useEffect } from 'react';
import {
  IntegrationCanvasNode,
  FlowExecutionState,
  NodeExecutionStatus,
  NodeExecutionResult,
  SyncJobStatus,
  ProductSyncDetail,
  TriggerConfig,
  MappingConfig,
  DestinationConfig,
} from '../types';

const NODE_TYPE_ORDER = ['trigger', 'source', 'mapping', 'destination'] as const;

const DEFAULT_STEP_DELAYS: Record<string, number> = {
  trigger: 800,
  source: 1000,
  mapping: 800,
};

const DEFAULT_RESET_DELAY = 0;
const DEFAULT_POLL_INTERVAL = 2000;

export interface FlowExecutionOptions {
  stepDelays?: Record<string, number>;
  resetDelay?: number;
  pollInterval?: number;
}

interface UseFlowExecutionReturn {
  executionState: FlowExecutionState;
  lastSync: SyncJobStatus | null;
  isRunning: boolean;
  startExecution: () => void;
  cancelReset: () => void;
}

const initialState: FlowExecutionState = {
  status: 'idle',
  nodeStatuses: {},
  nodeResults: {},
  syncStatus: null,
};

/** Build a human-readable result for each node based on its config */
const buildNodeResult = (node: IntegrationCanvasNode, syncStatus?: SyncJobStatus): NodeExecutionResult => {
  switch (node.type) {
    case 'trigger': {
      const cfg = node.config as TriggerConfig | null;
      const mode = cfg?.syncMode ?? 'manual';
      return {
        summary: mode === 'manual' ? 'Manual trigger' : mode === 'scheduled' ? `Scheduled (${cfg?.frequency ?? ''})` : 'Realtime',
        details: [
          { label: 'Mode', value: mode },
          ...(cfg?.frequency ? [{ label: 'Frequency', value: cfg.frequency }] : []),
        ],
      };
    }
    case 'source': {
      const connType = node.connectorType ?? 'unknown';
      return {
        summary: `${connType.charAt(0).toUpperCase() + connType.slice(1)} connected`,
        details: [
          { label: 'Connector', value: connType },
        ],
      };
    }
    case 'mapping': {
      const cfg = node.config as MappingConfig | null;
      const count = cfg?.mappings?.length ?? 0;
      return {
        summary: `${count} field${count !== 1 ? 's' : ''} mapped`,
        details: [
          { label: 'Fields mapped', value: count },
          { label: 'Auto-mapped', value: cfg?.autoMapped ? 'Yes' : 'No' },
        ],
      };
    }
    case 'destination': {
      if (syncStatus) {
        const summaryParts: string[] = [];
        if (syncStatus.createdProducts > 0) summaryParts.push(`${syncStatus.createdProducts} created`);
        if (syncStatus.updatedProducts > 0) summaryParts.push(`${syncStatus.updatedProducts} updated`);
        if (syncStatus.failedProducts > 0) summaryParts.push(`${syncStatus.failedProducts} failed`);
        if (summaryParts.length === 0) summaryParts.push(`${syncStatus.processedProducts} processed`);

        const result: NodeExecutionResult = {
          summary: summaryParts.join(', '),
          details: [
            { label: 'Total products', value: syncStatus.totalProducts },
            { label: 'Processed', value: syncStatus.processedProducts },
            ...(syncStatus.createdProducts > 0 ? [{ label: 'Created', value: syncStatus.createdProducts }] : []),
            { label: 'Updated', value: syncStatus.updatedProducts },
            { label: 'Skipped (no match)', value: syncStatus.skippedProducts ?? 0 },
            { label: 'Failed', value: syncStatus.failedProducts },
          ],
        };
        if (syncStatus.errors.length > 0) {
          result.errors = syncStatus.errors;
        }
        if (syncStatus.productDetails && syncStatus.productDetails.length > 0) {
          result.productDetails = syncStatus.productDetails;
        }
        return result;
      }
      const cfg = node.config as DestinationConfig | null;
      return {
        summary: `Match by ${cfg?.matchField ?? 'sku'}`,
        details: [
          { label: 'Match field', value: cfg?.matchField ?? 'sku' },
          { label: 'Sync fields', value: cfg?.syncFields?.length ?? 'All' },
        ],
      };
    }
    default:
      return { summary: 'Done' };
  }
};

export const useFlowExecution = (
  nodes: IntegrationCanvasNode[],
  api: {
    triggerSync: (flowId?: string) => Promise<{ jobId: string }>;
    getSyncStatus: (jobId: string) => Promise<SyncJobStatus>;
    getLatestSync?: () => Promise<SyncJobStatus | null>;
  },
  options?: FlowExecutionOptions,
): UseFlowExecutionReturn => {
  const stepDelays = options?.stepDelays ?? DEFAULT_STEP_DELAYS;
  const resetDelay = options?.resetDelay ?? DEFAULT_RESET_DELAY;
  const pollInterval = options?.pollInterval ?? DEFAULT_POLL_INTERVAL;

  const [executionState, setExecutionState] = useState<FlowExecutionState>(initialState);
  const [lastSync, setLastSync] = useState<SyncJobStatus | null>(null);
  const pollIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const resetTimerRef = useRef<NodeJS.Timeout | null>(null);
  const abortRef = useRef(false);

  // Load latest sync on mount
  useEffect(() => {
    if (api.getLatestSync) {
      api.getLatestSync().then((sync) => {
        if (sync) setLastSync(sync);
      }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (pollIntervalRef.current) clearInterval(pollIntervalRef.current);
      if (resetTimerRef.current) clearTimeout(resetTimerRef.current);
    };
  }, []);

  const setNodeStatus = (nodeId: string, status: NodeExecutionStatus) => {
    setExecutionState((prev) => ({
      ...prev,
      nodeStatuses: { ...prev.nodeStatuses, [nodeId]: status },
    }));
  };

  const setNodeResult = (nodeId: string, result: NodeExecutionResult) => {
    setExecutionState((prev) => ({
      ...prev,
      nodeResults: { ...prev.nodeResults, [nodeId]: result },
    }));
  };

  const cancelReset = useCallback(() => {
    if (resetTimerRef.current) {
      clearTimeout(resetTimerRef.current);
      resetTimerRef.current = null;
    }
  }, []);

  const resetAfterDelay = useCallback(() => {
    resetTimerRef.current = setTimeout(() => {
      setExecutionState(initialState);
    }, resetDelay);
  }, [resetDelay]);

  const delay = (ms: number) => new Promise((resolve) => setTimeout(resolve, ms));

  const startExecution = useCallback(async () => {
    if (executionState.status === 'running') return;
    abortRef.current = false;

    // Build ordered list of nodes matching the pipeline order
    const orderedNodes: IntegrationCanvasNode[] = [];
    for (const type of NODE_TYPE_ORDER) {
      const node = nodes.find((n) => n.type === type);
      if (node) orderedNodes.push(node);
    }

    if (orderedNodes.length === 0) return;

    // Initialize all nodes to idle, flow to running
    const nodeStatuses: Record<string, NodeExecutionStatus> = {};
    orderedNodes.forEach((n) => (nodeStatuses[n.id] = 'idle'));
    setExecutionState({ status: 'running', nodeStatuses, nodeResults: {}, syncStatus: null });

    // Simulate trigger → source → mapping steps
    for (const node of orderedNodes) {
      if (abortRef.current) return;

      if (node.type === 'destination') {
        // Real sync for destination node
        setNodeStatus(node.id, 'running');
        try {
          const flowId = node.flowId || orderedNodes[0]?.flowId;
          const result = await api.triggerSync(flowId);

          // Poll for sync status
          await new Promise<void>((resolve, reject) => {
            pollIntervalRef.current = setInterval(async () => {
              try {
                const status = await api.getSyncStatus(result.jobId);
                setExecutionState((prev) => ({ ...prev, syncStatus: status }));

                if (status.status === 'completed') {
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                  }
                  setLastSync(status);
                  setNodeResult(node.id, buildNodeResult(node, status));
                  resolve();
                } else if (status.status === 'failed') {
                  if (pollIntervalRef.current) {
                    clearInterval(pollIntervalRef.current);
                    pollIntervalRef.current = null;
                  }
                  setLastSync(status);
                  setNodeResult(node.id, buildNodeResult(node, status));
                  reject(new Error('Sync failed'));
                }
              } catch {
                // Continue polling on network error
              }
            }, pollInterval);
          });

          setNodeStatus(node.id, 'completed');
        } catch {
          setNodeStatus(node.id, 'failed');
          setExecutionState((prev) => ({
            ...prev,
            status: 'failed',
          }));
          return;
        }
      } else {
        // Simulated step
        setNodeStatus(node.id, 'running');
        await delay(stepDelays[node.type] ?? 800);
        if (abortRef.current) return;
        setNodeStatus(node.id, 'completed');
        setNodeResult(node.id, buildNodeResult(node));
      }
    }

    // All completed — keep results visible until next execution
    setExecutionState((prev) => ({
      ...prev,
      status: 'completed',
    }));
  }, [executionState.status, nodes, api, resetAfterDelay, stepDelays, pollInterval]);

  return {
    executionState,
    lastSync,
    isRunning: executionState.status === 'running',
    startExecution,
    cancelReset,
  };
};
